import { useState } from 'react';

import { addNotification } from 'notifications';
import { Button, TextField } from 'ui';
import { getApiErrorMessage } from 'utils/getApiErrorMessage';

import {
  useLinkTutorVk,
  useRefreshTutorVk,
  useTestTutorVk,
  useTutorVkStatus,
  useUnlinkTutorVk,
} from 'hooks/useTutorVk';

import classes from './TutorVkSettings.module.scss';

type Audience = 'tutor' | 'student';

export const TutorVkSettings = ({ audience = 'tutor' }: { audience?: Audience }) => {
  const status = useTutorVkStatus();
  const link = useLinkTutorVk();
  const unlink = useUnlinkTutorVk();
  const refresh = useRefreshTutorVk();
  const test = useTestTutorVk();
  const [profile, setProfile] = useState('');
  const data = status.data;

  const fail = (error: unknown, fallback: string) => {
    addNotification(getApiErrorMessage(error, fallback), 'error');
  };

  const waiting = !data && (status.isPending || status.isFetching);
  const failed = !data && status.isError;

  return (
    <section className={classes.block}>
      <h2>Уведомления ВКонтакте</h2>
      <p>
        {audience === 'student'
          ? 'Кроме почты можно получать сообщение, если репетитор откликнулся или написал в чат по заявке.'
          : 'Кроме почты можно получать сообщение о новой заявке и о первом сообщении в чате по заявке.'}
      </p>
      {waiting ? (
        <p>Проверяем привязку ВКонтакте.</p>
      ) : failed ? (
        <>
          <p>Не удалось загрузить статус. Нажмите еще раз.</p>
          <Button
            variant='filled'
            size='sm'
            shape='brick'
            color='secondary'
            type='button'
            onClick={() => {
              void status.refetch();
            }}
          >
            Повторить
          </Button>
        </>
      ) : !data?.vk_configured ? (
        <p>На сервере еще нет ключа сообщества. Когда он появится, здесь можно привязать профиль.</p>
      ) : data.vk_linked ? (
        <>
          <p>
            Профиль: {data.vk_screen_name || 'vk'}.{' '}
            {data.vk_can_message
              ? 'Сообщество может писать.'
              : 'Нужно разрешить сообщения сообщества.'}
          </p>
          <div className={classes.actions}>
            <a href={data.vk_community_url || 'https://vk.com/my_teacher_pro'} target='_blank' rel='noreferrer'>
              <Button variant='filled' size='sm' shape='brick' color='secondary' type='button'>
                Написать сообществу
              </Button>
            </a>
            <Button
              variant='filled'
              size='sm'
              shape='brick'
              color='secondary'
              type='button'
              disabled={refresh.isPending}
              onClick={() => {
                refresh.mutate(undefined, {
                  onSuccess: (next) => {
                    addNotification(
                      next.vk_can_message
                        ? 'Сообщество может вам писать.'
                        : 'Пока писать нельзя. Откройте диалог и разрешите сообщения.',
                      'success',
                    );
                  },
                  onError: (error) => fail(error, 'Не удалось проверить статус'),
                });
              }}
            >
              Обновить статус
            </Button>
            <Button
              variant='filled'
              size='sm'
              shape='brick'
              color='primary'
              type='button'
              disabled={test.isPending}
              onClick={() => {
                test.mutate(undefined, {
                  onSuccess: () => addNotification('Тестовое сообщение отправлено.', 'success'),
                  onError: (error) => fail(error, 'Не удалось отправить проверку'),
                });
              }}
            >
              Проверить
            </Button>
            <Button
              variant='filled'
              size='sm'
              shape='brick'
              color='secondary'
              type='button'
              disabled={unlink.isPending}
              onClick={() => {
                unlink.mutate(undefined, {
                  onSuccess: () => addNotification('Профиль ВК отвязан.', 'success'),
                  onError: (error) => fail(error, 'Не удалось отвязать профиль'),
                });
              }}
            >
              Отвязать
            </Button>
          </div>
        </>
      ) : (
        <>
          <p>Вставьте ссылку на профиль или юзернейм. Числовой id знать не нужно.</p>
          <TextField
            shape='brick'
            placeholder='vk.com/username'
            label='Профиль ВК'
            size='lg'
            value={profile}
            onChange={(value) => setProfile(String(value || ''))}
          />
          <Button
            variant='filled'
            size='sm'
            shape='brick'
            color='primary'
            type='button'
            disabled={link.isPending}
            onClick={() => {
              const value = profile.trim();
              if (!value) {
                addNotification('Вставьте ссылку или юзернейм.', 'error');
                return;
              }
              link.mutate(
                { profile: value },
                {
                  onSuccess: () => {
                    setProfile('');
                    addNotification('Профиль ВК сохранен. Разрешите сообщения и нажмите Проверить.', 'success');
                  },
                  onError: (error) => fail(error, 'Не удалось привязать ВК'),
                },
              );
            }}
          >
            Привязать
          </Button>
        </>
      )}
    </section>
  );
};
